import { can, useCurrentUser, type Permission } from "@roastery/auth";
import { useTranslation } from "@roastery/i18n";
import { Badge, cn } from "@roastery/ui";
import { Link } from "@tanstack/react-router";
import {
  ActivityIcon,
  BarChart3Icon,
  FileTextIcon,
  BuildingIcon,
  InboxIcon,
  LifeBuoyIcon,
  MegaphoneIcon,
  MessageSquareTextIcon,
  ReceiptIcon,
  ScrollTextIcon,
  ShieldIcon,
  TicketPercentIcon,
  UsersIcon,
  TriangleAlertIcon,
} from "lucide-react";
import type { ComponentType } from "react";
import { useExceptionCount } from "../features/exceptions/api";
import { useFeedbackStats } from "../features/feedback/api";
import { useOpenLeadCount } from "../features/leads/api";

interface NavigationItem {
  to: string;
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
  testId: string;
  /** Platform permission that opens this section; undefined = every operator who got this far. */
  permission?: Permission;
  /** Which running count to show beside it, if any. */
  badge?: "openLeads" | "exceptions" | "unreadFeedback";
}

/**
 * Sections of the operator's panel. The list grows with the epic; every entry is filtered by the same
 * permission the API checks, so an operator is never shown a door the backend keeps shut.
 */
const navigation: NavigationItem[] = [
  {
    to: "/",
    labelKey: "platform.nav.dashboard",
    icon: BarChart3Icon,
    testId: "nav-dashboard",
    permission: "platform.insights.read",
  },
  {
    to: "/tenants",
    labelKey: "platform.nav.tenants",
    icon: BuildingIcon,
    testId: "nav-tenants",
    permission: "platform.tenants.manage",
  },
  {
    to: "/leads",
    labelKey: "platform.nav.leads",
    icon: InboxIcon,
    testId: "nav-leads",
    permission: "platform.insights.read",
    badge: "openLeads",
  },
  {
    // EP-53: bugs, ideas and questions from roasteries; the badge counts what nobody here has read.
    to: "/feedback",
    labelKey: "platform.nav.feedback",
    icon: MessageSquareTextIcon,
    testId: "nav-feedback",
    permission: "platform.insights.read",
    badge: "unreadFeedback",
  },
  {
    to: "/plans",
    labelKey: "platform.nav.plans",
    icon: ReceiptIcon,
    testId: "nav-plans",
    permission: "platform.plans.manage",
  },
  {
    // EP-51. Visible while promotions are withheld from roasteries: the operator prepares them here.
    to: "/promotions",
    labelKey: "platform.nav.promotions",
    icon: TicketPercentIcon,
    testId: "nav-promotions",
    permission: "platform.plans.manage",
  },
  {
    to: "/exceptions",
    labelKey: "platform.nav.exceptions",
    icon: TriangleAlertIcon,
    testId: "nav-exceptions",
    permission: "platform.insights.read",
    badge: "exceptions",
  },
  {
    to: "/support",
    labelKey: "platform.nav.support",
    icon: LifeBuoyIcon,
    testId: "nav-support",
    permission: "platform.support.manage",
  },
  {
    to: "/announcements",
    labelKey: "platform.nav.announcements",
    icon: MegaphoneIcon,
    testId: "nav-announcements",
    permission: "platform.operations.manage",
  },
  {
    to: "/operators",
    labelKey: "platform.nav.operators",
    icon: UsersIcon,
    testId: "nav-operators",
    permission: "platform.operations.manage",
  },
  {
    to: "/reports",
    labelKey: "platform.nav.reports",
    icon: FileTextIcon,
    testId: "nav-reports",
    permission: "platform.insights.read",
  },
  {
    to: "/diagnostics",
    labelKey: "platform.nav.diagnostics",
    icon: ActivityIcon,
    testId: "nav-diagnostics",
    permission: "platform.operations.manage",
  },
  {
    to: "/audit",
    labelKey: "platform.nav.audit",
    icon: ScrollTextIcon,
    testId: "nav-audit",
    permission: "platform.insights.read",
  },
];

export function AdminSidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const canReadInsights = can(user, "platform.insights.read");
  const openLeads = useOpenLeadCount();
  const exceptions = useExceptionCount();
  const feedback = useFeedbackStats();

  return (
    <>
      {open ? (
        <button
          type="button"
          aria-label={t("nav.closeMenu")}
          data-testid="nav-scrim"
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
        />
      ) : null}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-60 shrink-0 flex-col border-r border-border bg-card print:hidden",
          "transition-transform duration-200 md:static md:z-auto md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
        data-testid="admin-sidebar"
      >
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <ShieldIcon className="size-5 text-primary" />
          <span className="text-sm font-semibold tracking-tight">{t("platform.shell.title")}</span>
        </div>

        <nav className="flex flex-1 flex-col gap-1 p-3">
          {navigation
            .filter(({ permission }) => !permission || can(user, permission))
            .map(({ to, labelKey, icon: Icon, testId, badge }) => {
              // Only when there is something to say: a badge showing zero is a badge that has stopped
              // meaning anything.
              let count = 0;
              if (canReadInsights) {
                if (badge === "openLeads") {
                  count = openLeads.data?.open ?? 0;
                } else if (badge === "exceptions") {
                  count = exceptions.data?.total ?? 0;
                } else if (badge === "unreadFeedback") {
                  count = feedback.data?.unread ?? 0;
                }
              }
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testId}
                  onClick={onClose}
                  activeOptions={{ exact: to === "/" }}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors",
                    "hover:bg-muted hover:text-foreground",
                  )}
                  activeProps={{ className: "bg-secondary text-secondary-foreground font-medium" }}
                >
                  <Icon className="size-4" />
                  {t(labelKey)}
                  {count > 0 ? (
                    <Badge variant="default" className="ml-auto" data-testid={`${testId}-badge`}>
                      {count}
                    </Badge>
                  ) : null}
                </Link>
              );
            })}
        </nav>
      </aside>
    </>
  );
}
